import { AxiosResponse, isAxiosError } from 'axios'
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'
import { NPosts } from '../interfaces/services/posts.interfaces'
import {
  PinnedPosts,
  PostRow,
  PostsPage,
  ScheduledPosts,
} from '../generated/api/post-row'
import { createUrl, definedParams } from './helpers'
import axiosAuth from './config'

export const isPostAccessError = (error: unknown): boolean => {
  const status = isAxiosError(error) ? error.response?.status : undefined
  return status === 403 || status === 404
}

export interface PostFeedFilter {
  societies: string[]
  postTypes: string[]
  searchTerm: string
}

const feedParams = (
  filter: PostFeedFilter,
  cursor: string | undefined
): { [key: string]: string | boolean } =>
  definedParams({
    societiesIds: filter.societies.join(';') || undefined,
    search: filter.searchTerm || undefined,
    isAlert: filter.postTypes.includes('isAlert') || undefined,
    resident: filter.postTypes.includes('resident') || undefined,
    boardMember: filter.postTypes.includes('board-member') || undefined,
    cursor,
  }) as { [key: string]: string | boolean }

export const getPostsFeed = (
  filter: PostFeedFilter,
  cursor: string | undefined,
  signal?: AbortSignal
): Promise<AxiosResponse<{ data: PostsPage }>> => {
  return axiosAuth.get<{ data: PostsPage }>(
    createUrl('/posts/feed', feedParams(filter, cursor)),
    { signal }
  )
}

export const getPinnedPostRows = (
  societies: string[],
  signal?: AbortSignal
): Promise<AxiosResponse<{ data: PinnedPosts }>> => {
  return axiosAuth.get<{ data: PinnedPosts }>(
    createUrl(
      '/posts/pinned',
      definedParams({ societiesIds: societies.join(';') || undefined })
    ),
    { signal }
  )
}

export const getScheduledPostRows = (
  signal?: AbortSignal
): Promise<AxiosResponse<{ data: ScheduledPosts }>> => {
  return axiosAuth.get<{ data: ScheduledPosts }>(
    createUrl('/posts/scheduled'),
    { signal }
  )
}

export const getPostRow = (
  id: string,
  signal?: AbortSignal
): Promise<AxiosResponse<{ data: PostRow }>> => {
  return axiosAuth.get<{ data: PostRow }>(createUrl(`/posts/${id}`), { signal })
}

export const toggleLike = (
  id: string,
  like: boolean
): Promise<AxiosResponse<NPosts.NToggleLike.IResponse>> => {
  return axiosAuth.post<NPosts.NToggleLike.IResponse>(
    createUrl(`/posts/toggle-like/${id}`),
    {
      value: like,
    }
  )
}

export const togglePin = (
  id: string,
  pin: boolean
): Promise<AxiosResponse<NPosts.NTogglePin.IResponse>> => {
  return axiosAuth.post<NPosts.NTogglePin.IResponse>(
    createUrl(`/posts/toggle-pin/${id}`),
    {
      value: pin,
    }
  )
}

export const getPost = (
  id: string
): Promise<AxiosResponse<NPosts.NGetById.IResponse>> => {
  return axiosAuth.get<NPosts.NGetById.IResponse>(
    createUrl(`/posts/post/${id}`, {})
  )
}

export const remove = (
  id: string
): Promise<AxiosResponse<NPosts.NDel.IResponse>> => {
  return axiosAuth.delete<NPosts.NDel.IResponse>(createUrl(`/posts/post/${id}`))
}

export const create = (
  body: NPosts.NCreate.IRequestBody
): Promise<AxiosResponse<NPosts.NCreate.IResponse>> => {
  return axiosAuth.post<NPosts.NCreate.IResponse>(
    createUrl('/posts/post'),
    body
  )
}

export const update = (
  id: string,
  body: NPosts.NPatch.IRequestBody
): Promise<AxiosResponse<NPosts.NPatch.IResponse>> => {
  return axiosAuth.patch<NPosts.NPatch.IResponse>(
    createUrl(`/posts/post/${id}`),
    body
  )
}

export const report = (variables: {
  id: string
  body: NPosts.NReport.IRequestBody
}): Promise<AxiosResponse<NPosts.NReport.IResponse>> => {
  return axiosAuth.post<NPosts.NReport.IResponse>(
    createUrl(`/posts/report/${variables.id}`),
    variables.body
  )
}

export const postQueries = {
  getPost: (postId?: string) =>
    queryOptions({
      queryKey: [{ scope: 'post', entity: 'detail', postId }],
      enabled: !!postId,
      queryFn: async ({ queryKey }) => {
        return (await getPost(queryKey[0].postId!)).data.data
      },
    }),
}

export const postRowsQueryKey = [{ scope: 'post' }] as const
export const postFeedQueryKey = [{ scope: 'post', entity: 'feed' }] as const
export const postPinnedQueryKey = [{ scope: 'post', entity: 'pinned' }] as const
export const postRowEntityKey = [{ scope: 'post', entity: 'row' }] as const
export const postScheduledQueryKey = [
  { scope: 'post', entity: 'scheduled' },
] as const

export const postRowQueries = {
  feed: (filter: PostFeedFilter) =>
    infiniteQueryOptions({
      queryKey: [{ ...postFeedQueryKey[0], filter }] as const,
      initialPageParam: undefined as string | undefined,
      queryFn: async ({ pageParam, queryKey, signal }) =>
        (await getPostsFeed(queryKey[0].filter, pageParam, signal)).data.data,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    }),
  pinned: (societies: string[]) =>
    queryOptions({
      queryKey: [{ ...postPinnedQueryKey[0], filter: { societies } }] as const,
      queryFn: async ({ queryKey, signal }) =>
        (await getPinnedPostRows(queryKey[0].filter.societies, signal)).data
          .data.items,
    }),
  scheduled: () =>
    queryOptions({
      queryKey: postScheduledQueryKey,
      queryFn: async ({ signal }) =>
        (await getScheduledPostRows(signal)).data.data.items,
      staleTime: 0,
    }),
  row: (postId?: string) =>
    queryOptions({
      queryKey: [{ scope: 'post', entity: 'row', postId }] as const,
      enabled: !!postId,
      queryFn: async ({ queryKey, signal }) =>
        (await getPostRow(queryKey[0].postId as string, signal)).data.data,
      retry: (failureCount, error) =>
        !isPostAccessError(error) && failureCount < 3,
    }),
}
